import React from "react";
import { Alert, Box, Typography } from "@mui/material";
import LocalPharmacyIcon from "@mui/icons-material/LocalPharmacy";
import PublicIcon from "@mui/icons-material/Public";
import NearMeIcon from "@mui/icons-material/NearMe";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SelectableChipList from "../SelectableChipList";
import type { PharmacySuggestionGroup } from "../../../utils/AIPrescription/prescriptionUtils";

/**
 * Copy + styling for each `pharmacy_search_fallback_mode` value from orchestration. This is a
 * fixed, known enum of structural signals (never free text or a scenario derived from parsing
 * the assistant's reply) -- the chat sentence itself is still LLM-authored separately via
 * `scenario_registry.py`; this banner is just a visual tag keyed off the same deterministic flag.
 */
const FALLBACK_BANNER_CONFIG: Record<
  string,
  { severity: "info" | "warning"; icon: React.ReactNode; message: string }
> = {
  nationwide_name_only: {
    severity: "warning",
    icon: <PublicIcon fontSize="small" />,
    message:
      "No pharmacy by that name was found near the patient — these are nationwide matches, so double-check the location before picking one.",
  },
  zip_area: {
    severity: "info",
    icon: <NearMeIcon fontSize="small" />,
    message: "No exact name match — showing pharmacies near the patient instead.",
  },
  state_area: {
    severity: "info",
    icon: <NearMeIcon fontSize="small" />,
    message: "No exact name match — showing pharmacies in the patient's state instead.",
  },
  mail_order_only: {
    severity: "info",
    icon: <LocalShippingIcon fontSize="small" />,
    message: "No local pharmacy location found — showing mail-order options for this pharmacy instead.",
  },
};

interface Pharmacy {
  text?: string;
  name?: string;
  pharmacyName?: string;
  value?: string;
  pharmacyId?: string;
  id?: string;
  pharmacy_name?: string;
  nameConcatenatedWithAddress?: string;
  [key: string]: any;
}

interface PharmacyLookupProps {
  pharmacies: (string | Pharmacy)[];
  /** `pharmacy_search_fallback_mode` from orchestration; drives the advisory banner below. */
  fallbackMode?: string | null;
  pharmacyGroups?: PharmacySuggestionGroup[];
  selectedPicksByMedication?: Record<string, string>;
  primaryColor: string;
  onPharmacySelect: (
    pharmacyName: string,
    pharmacyId?: string,
    medicationName?: string
  ) => void;
  label?: string;
  /** True when a medication-strength picker from the same turn is also awaiting a pick. */
  pendingOtherPicker?: boolean;
  /** Highlight for the bare (non-grouped) list -- the pharmacy already picked from it. */
  selectedPharmacyName?: string;
  /** True on a past (non-latest) turn: render read-only, chosen chip still highlighted. */
  disabled?: boolean;
}

const PharmacyLookup: React.FC<PharmacyLookupProps> = ({
  pharmacies,
  fallbackMode,
  pharmacyGroups,
  selectedPicksByMedication = {},
  primaryColor,
  onPharmacySelect,
  label = "Select Your Preferred Pharmacy:",
  pendingOtherPicker = false,
  selectedPharmacyName,
  disabled = false,
}) => {
  const extractId = (pharmacy: string | Pharmacy) => {
    if (typeof pharmacy === "string") return undefined;
    const id = pharmacy?.pharmacyId || pharmacy?.id;
    return id === undefined || id === null ? undefined : String(id);
  };

  // Visual-only chip label: pharmacy name with an address/city/state sub-line.
  // The value sent on select stays the plain name (handled by SelectableChipList).
  const renderPharmacyLabel = (pharmacy: string | Pharmacy): React.ReactNode => {
    if (typeof pharmacy === "string") return pharmacy;
    const name =
      pharmacy.pharmacyName ||
      pharmacy.name ||
      pharmacy.pharmacy_name ||
      pharmacy.text ||
      pharmacy.value ||
      "";
    const locationLine = [pharmacy.address, pharmacy.city, pharmacy.state]
      .filter((p) => typeof p === "string" && p.trim().length > 0)
      .join(", ");
    if (!locationLine) return name;
    return (
      <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.25 }}>
        <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
          {name}
        </Typography>
        <Typography component="span" variant="caption" sx={{ opacity: 0.8 }}>
          {locationLine}
        </Typography>
      </Box>
    );
  };

  const renderFallbackBanner = (mode?: string | null): React.ReactNode => {
    const config = mode ? FALLBACK_BANNER_CONFIG[mode] : undefined;
    if (!config) return null;
    return (
      <Alert
        severity={config.severity}
        icon={config.icon}
        variant="outlined"
        sx={{
          alignItems: "center",
          py: 0.25,
          "& .MuiAlert-message": { fontSize: "0.8125rem", lineHeight: 1.4 },
        }}
      >
        {config.message}
      </Alert>
    );
  };
  const fallbackBanner = renderFallbackBanner(fallbackMode);

  if (pharmacyGroups && pharmacyGroups.length > 0) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 2, mb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Select Pharmacy:
        </Typography>
        {pendingOtherPicker && (
          <Typography variant="body2" sx={{ color: "#495057", lineHeight: 1.5, mt: -1 }}>
            Pick a pharmacy below, and a medication strength above.
          </Typography>
        )}
        {pharmacyGroups.map((group) => (
          <Box key={group.medication_name} sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {renderFallbackBanner(group.fallback_mode)}
            <SelectableChipList
              items={group.options}
              primaryColor={primaryColor}
              onItemSelect={(name, id) =>
                onPharmacySelect(name, id, group.medication_name)
              }
              label={`${group.medication_name}:`}
              icon={<LocalPharmacyIcon sx={{ fontSize: "16px !important" }} />}
              extractId={extractId}
              getItemLabel={renderPharmacyLabel}
              selectedItems={
                selectedPicksByMedication[group.medication_name]
                  ? [selectedPicksByMedication[group.medication_name]]
                  : []
              }
              disabled={disabled}
            />
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {fallbackBanner}
      <SelectableChipList
        items={pharmacies}
        primaryColor={primaryColor}
        onItemSelect={onPharmacySelect}
        label={label}
        icon={<LocalPharmacyIcon sx={{ fontSize: "16px !important" }} />}
        extractId={extractId}
        getItemLabel={renderPharmacyLabel}
        selectedItems={selectedPharmacyName ? [selectedPharmacyName] : []}
        disabled={disabled}
      />
    </Box>
  );
};

export default PharmacyLookup;

